import { Col, Form, Row } from "react-bootstrap";
import { FormEvent, useEffect, useState } from 'react'
import { CampaignDetailsDto } from "../types";
import { useTranslation } from "react-i18next";
import { getCampaignDetails, saveCampaign, updateCampaign } from "../api/campaignAPI";
import { useNavigate, useParams } from 'react-router-dom';
import { InputDatePicker } from "../../../../components/InputDatePicker";
import { useUsersSelect } from "../../../../hooks/getUsersByClient";
import { ApiErrorResponse } from "../../../../types/ApiErrorResponse";
import { useAlert } from "../../../../hooks/useAlert";
import AlertMessage from "../../../../components/AlertMessage";
import { getCampaignFormSchema } from "../schema/campaignSchema";
import { CampaignStatusEnum } from "../types/CampaignStatusEnum";
import { GenericSaveButton } from "../../../../components/common/GenericSaveButton";

export default function NewCampaign() {
    const { t } = useTranslation(['common', 'campaign']);
    const navigate = useNavigate();

    const { campaignId } = useParams<{ campaignId: string }>();
    const isEditMode = Boolean(campaignId);

    const { alert, hideAlert, showAlert, isVisible } = useAlert({ fromLocation: false });
    const [readOnly, setReadOnly] = useState<boolean>(false);
    const [errors, setErrors] = useState<{ [key: string]: string | undefined }>({});

    const [campaignForm, setCampaignForm] = useState<CampaignDetailsDto>({
        id: 0,
        name:""
    })

    const changeInputField = (field: keyof CampaignDetailsDto) => (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
    ) => {
        setCampaignForm({
            ...campaignForm,
            [field]: e.target.value
        });
    };

    useEffect(() => {
        if (campaignId) {
            getCampaignDetails(parseInt(campaignId))
                .then(data => {
                    setCampaignForm(data);
                    setReadOnly(data.status == CampaignStatusEnum.Closed)
                })
                .catch((err: ApiErrorResponse) => {
                    console.error(err);
                });
        }
       
    }, [campaignId]);

    const { data: users } = useUsersSelect({});
    
    const handleSubmit = async (e?: FormEvent) => {
        e?.preventDefault();
        const schema = getCampaignFormSchema(t);

        try {
            await schema.validate(campaignForm, { abortEarly: false });
        } catch (err: any) {
            const validationErrors: { [key: string]: string | undefined } = {};
            err.inner?.forEach((error: any) => {
                if (error.path) {
                    validationErrors[error.path] = error.message;
                }
            });
            setErrors(validationErrors);
            return;
        }

        try {
            if (isEditMode) {
                await updateCampaign(campaignForm.id,campaignForm);
                navigate('/admin/campaign', {
                    state: { message: t("campaign:new.newCampaignSaveSuccessfully"), shouldRefresh: true }
                });
            }else{
                await saveCampaign(campaignForm);
                navigate('/admin/campaign', {
                    state: { message: t("campaign:new.newCampaignSaveSuccessfully"), shouldRefresh: true }
                });
            }
        } catch (error) {
            const fetchError = error as ApiErrorResponse;
            const errorMessage = fetchError?.message
            showAlert({
                message: errorMessage,
                type: "danger",
                duration: 5000,
            });
        }
    }

    return (
        <>
            <Row className="mb-2">
                <Col xs={12} md={8}>
                    <h2>{isEditMode
                        ? t("campaign:new.titleEditCampaign")
                        : t("campaign:new.titleNewCampaign")}</h2>
                </Col>
                <Col xs={4} md={4} className="text-end">
                    <GenericSaveButton
                        onClick={() => handleSubmit()}
                        onCancelClick={() => navigate('/admin/campaign')}
                        cancelLabel={isEditMode ? t("common:buttons.buttonBack") : t("common:buttons.buttonCancel")}
                    />
                </Col>
                {isVisible && (
                    <AlertMessage
                        message={alert!.message}
                        type={alert!.type}
                        onClose={hideAlert}
                    />
                )}
            </Row>
            <Row>
                <Col>
                    <Form>
                        
                        <Row>
                            <Col xs={12} md={12}>
                                <h5>{t("campaign:new.newCampaignHeader1")}: {t("campaign:new.newCampaignHeader2")}</h5>
                            </Col>
                        </Row>
                        <Row style={{ marginTop: '20px' }}>
                            <Col xs={4} md={4}>
                                <Form.Group>
                                    <Form.Label>{t("campaign:new.newCampaignInputIdExtenal")}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={campaignForm.idExternal}
                                        onChange={changeInputField('idExternal')}
                                        readOnly={readOnly}
                                    />
                                </Form.Group>
                            </Col>
                        
                            <Col xs={8} md={8}>
                                <Form.Group>
                                    <Form.Label>{t("campaign:new.newCampaignInputName")}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={campaignForm.name}
                                        onChange={changeInputField('name')}
                                        isInvalid={!!errors["name"]}
                                        readOnly={readOnly}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.name || ""}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row style={{ marginTop: '20px' }}>
                            <Col xs={12} md={12}>
                                <Form.Group>
                                    <Form.Label>{t("campaign:new.newCampaignInputDescription")}</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={5} 
                                        value={campaignForm.description}
                                        onChange={changeInputField('description')}
                                        readOnly={readOnly}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row style={{ marginTop: '20px' }}>
                            <Col xs={4} md={4}>
                                    <Form.Group className="mb-3">
                                        <InputDatePicker
                                            value={campaignForm.startDate ? new Date(campaignForm.startDate) : null}
                                            onChange={(date: Date | null) => {
                                                setCampaignForm(prev => ({
                                                    ...prev,
                                                    startDate: date ? date.toISOString() : null
                                                }));
                                            }}
                                            label={t("campaign:new.newCampaignInputStartDate")}
                                            hasError={'startDate' in errors && !!errors.startDate}
                                            errorMessage={errors?.startDate}
                                            disabled={readOnly}
                                        />
                                    </Form.Group>
                            </Col>
                            <Col xs={4} md={4}>
                                <Form.Group className="mb-3">
                                    <InputDatePicker
                                        value={campaignForm.endDate ? new Date(campaignForm.endDate) : null}
                                        onChange={(date: Date | null) => {
                                            setCampaignForm(prev => ({
                                                ...prev,
                                                endDate: date ? date.toISOString() : null
                                            }));
                                        }}
                                        label={t("campaign:new.newCampaignInputFinishDate")}
                                        hasError={'endDate' in errors && !!errors.endDate}
                                        errorMessage={errors?.endDate}
                                        disabled={readOnly}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row style={{ marginTop: '20px' }}>
                            <Col xs={6} md={6}>
                                <Form.Label>{t("campaign:new.newCampaignInputStatus")}</Form.Label>
                                <Form.Control
                                    as="select"
                                    className={`form-select`}
                                    value={campaignForm.status}
                                    readOnly={readOnly}
                                    onChange={(e) => {
                                        setCampaignForm(prev => ({
                                            ...prev,
                                            status: Number(e.target.value)
                                        }));
                                    }}
                                >
                                    {Object.entries(CampaignStatusEnum)
                                        .filter(([, value]) => typeof value === 'number')
                                        .map(([key, value]) => (
                                            <option key={value} value={value}>
                                                {t(`campaign:status.${key}`)}
                                            </option>
                                        ))
                                    }
                                </Form.Control>
                            </Col>
                            <Col xs={6} md={6}>
                                <Form.Label>{t("campaign:new.newCampaignInputResponsableUser")}</Form.Label>
                                <Form.Control
                                    as="select"
                                    className={`form-select`}
                                    readOnly={readOnly}
                                    value={campaignForm.responsableUser ?? ""}
                                    onChange={(e) => {
                                        setCampaignForm(prev => ({
                                            ...prev,
                                            responsableUser: Number(e.target.value)
                                        }));
                                    }}
                                >
                                    <option  value="" ></option>
                                    {users?.map((user) => (
                                        <option key={user.id} value={user.id} >{user.name}</option>
                                    ))}

                                </Form.Control>
                            </Col>
                        </Row>
                        <Row style={{ marginTop: '40px' }}>
                            <Col xs={12} md={12}>
                                <h5>{t("campaign:new.newCampaignHeader3")}</h5>
                            </Col>
                        </Row>
                        <Row style={{ marginTop: '20px' }}>
                            <Col xs={6} md={6}>
                                <Form.Group>
                                    <Form.Label>{t("campaign:new.newCampaignInputTargetAudience")}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={campaignForm.targetAudience}
                                        readOnly={readOnly}
                                        onChange={changeInputField('targetAudience')}
                                    />
                                </Form.Group>
                            </Col>
                            <Col xs={6} md={6}>
                                <Form.Group>
                                    <Form.Label>{t("campaign:new.newCampaignInputTextLocation")}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={campaignForm.location}
                                        readOnly={readOnly}
                                        onChange={changeInputField('location')}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row style={{ marginTop: '20px' }}>
                            <Col xs={6} md={6}>
                                <Form.Group>
                                    <Form.Label>{t("campaign:new.newCampaignInputSegmentationCriteria")}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={campaignForm.segmentationCriteria}
                                        readOnly={readOnly}
                                        onChange={changeInputField('segmentationCriteria')}
                                    />
                                </Form.Group>
                            </Col>
                            <Col xs={6} md={6}>
                                <Form.Group>
                                    <Form.Label>{t("campaign:new.newCampaignInputContent")}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={campaignForm.content}
                                        readOnly={readOnly}
                                        onChange={changeInputField('content')}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row style={{ marginTop: '20px' }}>
                            <Col xs={6} md={6}>
                                <Form.Group>
                                    <Form.Label>{t("campaign:new.newCampaignInputContentType")}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={campaignForm.contentType}
                                        readOnly={readOnly}
                                        onChange={changeInputField('contentType')}
                                    />
                                </Form.Group>
                            </Col>
                            <Col xs={6} md={6}>
                                <Form.Label>{t("campaign:new.newCampaignInputDeliveryChanel")}</Form.Label>
                                <Form.Control
                                    as="select"
                                    className={`form-select`}
                                    value={campaignForm.deliveryChanel ?? -1}
                                    readOnly={readOnly}
                                    onChange={(e) => {
                                        setCampaignForm(prev => ({
                                            ...prev,
                                            deliveryChanel: Number(e.target.value)
                                        }));
                                    }}>
                                    <option value="-1"></option>
                                    <option value="0">Email</option>
                                    <option value="1">Landing page</option>
                                    <option value="2">SMS</option>
                                    <option value="3">XXSS</option>

                                </Form.Control>
                            </Col>
                        </Row>
                        <Row style={{ marginTop: '20px' }}>
                            <Col xs={6} md={6}>
                                <Form.Group>
                                    <Form.Label>{t("campaign:new.newCampaignInputTextPlaning")}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        readOnly={readOnly}
                                        value={campaignForm.planing}
                                        onChange={changeInputField('planing')}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row style={{ marginTop: '40px' }}>
                            <Col xs={12} md={12}>
                                <h5>{t("campaign:new.newCampaignHeader4")}</h5>
                            </Col>
                        </Row>
                        <Row style={{ marginTop: '20px' }}>
                            <Col xs={6} md={6}>
                                <Form.Group>
                                    <Form.Label>{t("campaign:new.newCampaignInputTextBudget")}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={campaignForm.budget ?? ""}
                                        readOnly={readOnly}
                                        onChange={changeInputField('budget')}
                                    />
                                </Form.Group>
                            </Col>
                            <Col xs={6} md={6}>
                                <Form.Group>
                                    <Form.Label>{t("campaign:new.newCampaignInputTextRealExpense")}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={campaignForm.realExpense ?? ""}
                                        readOnly={readOnly}
                                        onChange={changeInputField('realExpense')}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row style={{ marginTop: '20px' }}>
                            <Col xs={6} md={6}>
                                <Form.Group>
                                    <Form.Label>{t("campaign:new.newCampaignInputTextExpectedIncome")}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={campaignForm.expectedIncome ?? ""}
                                        readOnly={readOnly}
                                        onChange={changeInputField('expectedIncome')}
                                    />
                                </Form.Group>
                            </Col>
                            <Col xs={6} md={6}>
                                <Form.Group>
                                    <Form.Label>{t("campaign:new.newCampaignInputTextRealIncome")}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={campaignForm.realIncome ?? ""}
                                        readOnly={readOnly}
                                        onChange={changeInputField('realIncome')}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>                  
                    </Form>
                </Col>
            </Row>
        </>
    );
}

